import {combineLatest, merge} from 'rxjs';
import {distinctUntilChanged, filter, map, scan, startWith} from 'rxjs/operators';
import {bookMsg$} from '@/raw/book';
import {tradePair$} from '@/state/trade-pair';
import {genSortedPriceEps} from './util';
import type {ReduceAction, TradePair, ws, BookCaches, BookCache, BookItemRaw} from '@/types';

type BookMsg = ws.BookMsg;

const resetCachesAction$ = tradePair$.pipe(distinctUntilChanged(), map(convertResetCachesAction));

const currentBookMsg$ = combineLatest([bookMsg$, tradePair$]).pipe(
  filter(([bookMsg, tradePair]: [BookMsg, TradePair]) => bookMsg.symbol === tradePair.symbol),
  map((o: [BookMsg, TradePair]): BookMsg => o[0])
);

const updateCachesBySnapshotAction$ = currentBookMsg$.pipe(
  filter((msg: BookMsg) => msg.type === 'snapshot'),
  map(convertUpdateCachesBySnapshotAction)
);

const updateCachesByIncrementalAction$ = currentBookMsg$.pipe(
  filter((msg: BookMsg) => msg.type === 'incremental'),
  map(convertUpdateCachesIncrementalAction)
);

export const bookCaches$ = merge(
  resetCachesAction$,
  updateCachesBySnapshotAction$,
  updateCachesByIncrementalAction$
).pipe(
  startWith<ReduceAction<BookCaches>>((x: BookCaches): BookCaches => x),
  scan((p: BookCaches, c: ReduceAction<BookCaches>) => c(p), createInitBookCaches()),
  map(convertBookCaches)
);

function convertResetCachesAction(tradePair: TradePair): ReduceAction<BookCaches> {
  return function (bookCaches: BookCaches): BookCaches {
    const {bids, asks} = bookCaches;
    bids.clear();
    asks.clear();
    return {tradePair, bids, asks};
  };
}

function convertUpdateCachesBySnapshotAction(msg: BookMsg): ReduceAction<BookCaches> {
  return function (bookCaches: BookCaches): BookCaches {
    const {tradePair, bids, asks} = bookCaches;
    bids.clear();
    asks.clear();

    const {book} = msg;
    updateCache(bids, book.bids as BookItemRaw[]);
    updateCache(asks, book.asks as BookItemRaw[]);

    return {tradePair, bids, asks};
  };
}

function convertUpdateCachesIncrementalAction(msg: BookMsg): ReduceAction<BookCaches> {
  return function (bookCaches: BookCaches): BookCaches {
    const {tradePair, bids, asks} = bookCaches;

    const {book} = msg;
    updateCache(bids, book.bids as BookItemRaw[]);
    updateCache(asks, book.asks as BookItemRaw[]);

    // 修正交叉记录
    cleanBidCache(bids, book.asks);
    cleanAskCache(asks, book.bids);

    return {tradePair, bids, asks};
  };
}

function createInitBookCaches(): BookCaches {
  return {tradePair: null, bids: new Map(), asks: new Map()};
}

function convertBookCaches({tradePair, bids, asks}: BookCaches): BookCaches {
  return {
    tradePair,
    bids,
    bidPriceEps: genSortedPriceEps(bids, 'Bid'),
    asks,
    askPriceEps: genSortedPriceEps(asks, 'Ask'),
  };
}

function updateCache(cache: BookCache, items: BookItemRaw[]) {
  items.forEach((item: BookItemRaw) => updateCacheItem(cache, item));
}

function updateCacheItem(cache: BookCache, item: BookItemRaw) {
  if (item[1] === 0) {
    cache.delete(item[0]);
    return;
  }

  cache.set(item[0], item[1]);
}

function cleanBidCache(bidCache: BookCache, asks: BookItemRaw[]) {
  if (asks.length > 0) {
    const minAskPriceEp = Math.min(...calcPriceEps(asks));
    [...bidCache.keys()]
      .filter((priceEp: number) => priceEp >= minAskPriceEp)
      .forEach((priceEp: number) => bidCache.delete(priceEp));
  }
}

function cleanAskCache(askCache: BookCache, bids: BookItemRaw[]) {
  if (bids.length > 0) {
    const maxBidPriceEp = Math.max(...calcPriceEps(bids));
    [...askCache.keys()]
      .filter((priceEp: number) => priceEp <= maxBidPriceEp)
      .forEach((priceEp: number) => askCache.delete(priceEp));
  }
}

/**
 * 获取 (size >0) 价格列表
 */
function calcPriceEps(items: BookItemRaw[]) {
  return items.filter((o: BookItemRaw) => o[1] > 0).map((item: BookItemRaw) => item[0]);
}
